import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Users, Briefcase, TrendingUp, Plus, Search, Eye, Edit, Trash2,
  DollarSign, PieChart, Calendar, Building, Percent, Clock, CheckCircle,
  AlertCircle, UserPlus, FolderPlus, Settings, ArrowRight, Receipt, 
  Wallet, ArrowUpCircle, ArrowDownCircle, Calculator, FileCheck
} from 'lucide-react';
import {
  listInvestors, createInvestor, updateInvestor, deleteInvestor,
  listInvestorPools, createInvestorPool, updateInvestorPool, getInvestorPool,
  getPoolRules, updatePoolRules, addPoolInvestment, getInvestorPoolStats,
  getPoolFinancialSummary, listPoolRevenue, addPoolRevenue, listPoolExpenses,
  addPoolExpense, deletePoolExpense, listPoolPayouts, createPoolPayout,
  approvePoolPayout, calculateDistribution, generatePayouts
} from '../lib/api';

const statusColors = {
  open: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800',
  active: 'bg-blue-100 text-blue-800',
  completed: 'bg-purple-100 text-purple-800',
  pending: 'bg-yellow-100 text-yellow-800',
  inactive: 'bg-red-100 text-red-800',
};

export const InvestorPoolsPage = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [pools, setPools] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Dialog states
  const [showPoolDialog, setShowPoolDialog] = useState(false);
  const [showInvestorDialog, setShowInvestorDialog] = useState(false);
  const [showPoolDetailsDialog, setShowPoolDetailsDialog] = useState(false);
  const [showInvestmentDialog, setShowInvestmentDialog] = useState(false);
  const [showRulesDialog, setShowRulesDialog] = useState(false);
  
  const [selectedPool, setSelectedPool] = useState(null);
  const [poolDetails, setPoolDetails] = useState(null);
  const [poolRules, setPoolRules] = useState(null);

  // Form states
  const [poolForm, setPoolForm] = useState({
    pool_name: '',
    pool_description: '',
    pool_target_amount: '',
    pool_minimum_investment: '100000',
    pool_duration_months: '24'
  });

  const [investorForm, setInvestorForm] = useState({
    name: '',
    email: '',
    phone: '',
    nationality: '',
    address: '',
    username: '',
    password: ''
  });

  const [investmentForm, setInvestmentForm] = useState({
    investor_id: '',
    investment_amount: '',
    investment_date: new Date().toISOString().split('T')[0]
  });

  const [rulesForm, setRulesForm] = useState({
    profit_share_company_percent: 55,
    profit_share_investor_percent: 45,
    document_share_company_percent: 50,
    document_share_investor_percent: 50,
    payout_frequency: 'monthly'
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, poolsRes, investorsRes] = await Promise.all([
        getInvestorPoolStats(),
        listInvestorPools(),
        listInvestors()
      ]);
      setStats(statsRes.data);
      setPools(poolsRes.data);
      setInvestors(investorsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Pool handlers
  const handleCreatePool = async (e) => {
    e.preventDefault();
    try {
      await createInvestorPool({
        ...poolForm,
        pool_target_amount: parseFloat(poolForm.pool_target_amount),
        pool_minimum_investment: parseFloat(poolForm.pool_minimum_investment),
        pool_duration_months: parseInt(poolForm.pool_duration_months)
      });
      toast.success('Pool created successfully');
      setShowPoolDialog(false);
      setPoolForm({
        pool_name: '',
        pool_description: '',
        pool_target_amount: '',
        pool_minimum_investment: '100000',
        pool_duration_months: '24'
      });
      loadData();
    } catch (error) {
      toast.error('Failed to create pool');
    }
  };

  const handleViewPool = async (pool) => {
    try {
      const res = await getInvestorPool(pool.id);
      setPoolDetails(res.data);
      setSelectedPool(pool);
      setShowPoolDetailsDialog(true);
    } catch (error) {
      toast.error('Failed to load pool details');
    }
  };

  const handleViewRules = async (pool) => {
    try {
      const res = await getPoolRules(pool.id);
      setPoolRules(res.data);
      setSelectedPool(pool);
      setRulesForm({
        profit_share_company_percent: res.data.profit_share_company_percent,
        profit_share_investor_percent: res.data.profit_share_investor_percent,
        document_share_company_percent: res.data.document_share_company_percent,
        document_share_investor_percent: res.data.document_share_investor_percent,
        payout_frequency: res.data.payout_frequency
      });
      setShowRulesDialog(true);
    } catch (error) {
      toast.error('Failed to load pool rules');
    }
  };

  const handleUpdateRules = async (e) => {
    e.preventDefault();
    try {
      await updatePoolRules(selectedPool.id, rulesForm);
      toast.success('Rules updated successfully');
      setShowRulesDialog(false);
      loadData();
    } catch (error) {
      toast.error('Failed to update rules');
    }
  };

  // Investor handlers
  const handleCreateInvestor = async (e) => {
    e.preventDefault();
    try {
      await createInvestor(investorForm);
      toast.success('Investor created successfully');
      setShowInvestorDialog(false);
      setInvestorForm({
        name: '',
        email: '',
        phone: '',
        nationality: '',
        address: '',
        username: '',
        password: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create investor');
    }
  };

  // Investment handlers
  const handleAddInvestment = async (e) => {
    e.preventDefault();
    try {
      await addPoolInvestment(selectedPool.id, {
        ...investmentForm,
        investment_amount: parseFloat(investmentForm.investment_amount)
      });
      toast.success('Investment added successfully');
      setShowInvestmentDialog(false);
      setInvestmentForm({
        investor_id: '',
        investment_amount: '',
        investment_date: new Date().toISOString().split('T')[0]
      });
      loadData();
      // Refresh pool details if open
      if (showPoolDetailsDialog && selectedPool) {
        handleViewPool(selectedPool);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add investment');
    }
  };

  const openInvestmentDialog = (pool) => {
    setSelectedPool(pool);
    setShowInvestmentDialog(true);
  };

  // Filter
  const filteredPools = pools.filter(p => 
    p.pool_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredInvestors = investors.filter(i =>
    i.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight" data-testid="investor-pools-title">
            Investor Pools
          </h1>
          <p className="text-muted-foreground mt-1">Manage investor-funded asset pools</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-2xl grid-cols-4">
          <TabsTrigger value="overview" data-testid="tab-overview">
            <PieChart className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="pools" data-testid="tab-pools">
            <Briefcase className="h-4 w-4 mr-2" />
            Pools
          </TabsTrigger>
          <TabsTrigger value="investors" data-testid="tab-investors">
            <Users className="h-4 w-4 mr-2" />
            Investors
          </TabsTrigger>
          <TabsTrigger value="finance" data-testid="tab-finance">
            <Calculator className="h-4 w-4 mr-2" />
            Finance
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Briefcase className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats?.total_pools || 0}</p>
                    <p className="text-sm text-muted-foreground">Total Pools</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <Users className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats?.active_investors || 0}</p>
                    <p className="text-sm text-muted-foreground">Active Investors</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <DollarSign className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {(stats?.total_raised_amount || 0).toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground">Total Raised (SAR)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats?.funding_progress_percent || 0}%</p>
                    <p className="text-sm text-muted-foreground">Funding Progress</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Pool Status Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Pool Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <span className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-green-500 rounded-full" />
                      Open Pools
                    </span>
                    <span className="font-bold">{stats?.pools_by_status?.open || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <span className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-blue-500 rounded-full" />
                      Active Pools
                    </span>
                    <span className="font-bold">{stats?.pools_by_status?.active || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-gray-500 rounded-full" />
                      Closed Pools
                    </span>
                    <span className="font-bold">{stats?.pools_by_status?.closed || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                    <span className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-purple-500 rounded-full" />
                      Completed Pools
                    </span>
                    <span className="font-bold">{stats?.pools_by_status?.completed || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  className="w-full justify-start" 
                  variant="outline"
                  onClick={() => setShowPoolDialog(true)}
                  data-testid="quick-create-pool"
                >
                  <FolderPlus className="h-4 w-4 mr-2" />
                  Create New Pool
                </Button>
                <Button 
                  className="w-full justify-start" 
                  variant="outline"
                  onClick={() => setShowInvestorDialog(true)}
                  data-testid="quick-create-investor"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add New Investor
                </Button>
                <Button 
                  className="w-full justify-start" 
                  variant="outline"
                  onClick={() => setActiveTab('pools')}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  View All Pools
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Recent Pools */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Pools</CardTitle>
            </CardHeader>
            <CardContent>
              {pools.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Briefcase className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No pools created yet</p>
                  <Button 
                    className="mt-4" 
                    onClick={() => setShowPoolDialog(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create First Pool
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {pools.slice(0, 5).map(pool => (
                    <div 
                      key={pool.id} 
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/30 cursor-pointer"
                      onClick={() => handleViewPool(pool)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Briefcase className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-semibold">{pool.pool_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {pool.investor_count || 0} investors • {pool.asset_count || 0} assets
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={statusColors[pool.status] || 'bg-gray-100'}>
                          {pool.status}
                        </Badge>
                        <p className="text-sm text-muted-foreground mt-1">
                          {((pool.pool_raised_amount / pool.pool_target_amount) * 100 || 0).toFixed(0)}% funded
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pools Tab */}
        <TabsContent value="pools" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search pools..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button onClick={() => setShowPoolDialog(true)} data-testid="create-pool-btn">
              <Plus className="h-4 w-4 mr-2" />
              Create Pool
            </Button>
          </div>

          <div className="grid gap-4">
            {filteredPools.map(pool => (
              <Card key={pool.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-purple-100 rounded-lg">
                        <Briefcase className="h-6 w-6 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg">{pool.pool_name}</h3>
                        <p className="text-sm text-muted-foreground">{pool.pool_description || 'No description'}</p>
                        <div className="flex gap-4 mt-2 text-sm">
                          <span className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            {pool.investor_count || 0} investors
                          </span>
                          <span className="flex items-center gap-1">
                            <Building className="h-4 w-4" />
                            {pool.asset_count || 0} assets
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {pool.pool_duration_months} months
                          </span>
                        </div>
                      </div>
                    </div>
                    <Badge className={statusColors[pool.status] || 'bg-gray-100'}>
                      {pool.status}
                    </Badge>
                  </div>

                  {/* Progress bar */}
                  <div className="mt-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span>Raised: {pool.pool_raised_amount?.toLocaleString()} SAR</span>
                      <span>Target: {pool.pool_target_amount?.toLocaleString()} SAR</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-purple-600 rounded-full transition-all"
                        style={{ width: `${Math.min((pool.pool_raised_amount / pool.pool_target_amount) * 100 || 0, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {((pool.pool_raised_amount / pool.pool_target_amount) * 100 || 0).toFixed(1)}% of target
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" variant="outline" onClick={() => handleViewPool(pool)}>
                      <Eye className="h-4 w-4 mr-1" />
                      View Details
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openInvestmentDialog(pool)}>
                      <UserPlus className="h-4 w-4 mr-1" />
                      Add Investment
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleViewRules(pool)}>
                      <Settings className="h-4 w-4 mr-1" />
                      Rules
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}

            {filteredPools.length === 0 && (
              <Card className="p-12 text-center">
                <Briefcase className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No pools found</h3>
                <p className="text-muted-foreground mb-4">Create your first investment pool</p>
                <Button onClick={() => setShowPoolDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Pool
                </Button>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Investors Tab */}
        <TabsContent value="investors" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search investors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button onClick={() => setShowInvestorDialog(true)} data-testid="create-investor-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Investor
            </Button>
          </div>

          <div className="grid gap-4">
            {filteredInvestors.map(investor => (
              <Card key={investor.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-green-100 rounded-lg">
                        <Users className="h-6 w-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg">{investor.name}</h3>
                        <p className="text-sm text-muted-foreground">{investor.email}</p>
                        <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
                          <span>{investor.phone}</span>
                          {investor.nationality && <span>• {investor.nationality}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={statusColors[investor.status] || 'bg-gray-100'}>
                        {investor.status}
                      </Badge>
                      {investor.kyc_verified && (
                        <div className="flex items-center gap-1 text-green-600 text-xs mt-1">
                          <CheckCircle className="h-3 w-3" />
                          KYC Verified
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {filteredInvestors.length === 0 && (
              <Card className="p-12 text-center">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No investors found</h3>
                <p className="text-muted-foreground mb-4">Add your first investor</p>
                <Button onClick={() => setShowInvestorDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Investor
                </Button>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Finance Tab */}
        <TabsContent value="finance" className="space-y-6">
          <FinanceTab pools={pools} investors={investors} />
        </TabsContent>
      </Tabs>

      {/* Create Pool Dialog */}
      <Dialog open={showPoolDialog} onOpenChange={setShowPoolDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-purple-600" />
              Create Investment Pool
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePool} className="space-y-4">
            <div>
              <Label>Pool Name *</Label>
              <Input
                value={poolForm.pool_name}
                onChange={(e) => setPoolForm({ ...poolForm, pool_name: e.target.value })}
                placeholder="Heavy Equipment Pool 2026"
                required
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={poolForm.pool_description}
                onChange={(e) => setPoolForm({ ...poolForm, pool_description: e.target.value })}
                placeholder="Pool of 10 heavy equipment machines for rental operations"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Target Amount (SAR) *</Label>
                <Input
                  type="number"
                  value={poolForm.pool_target_amount}
                  onChange={(e) => setPoolForm({ ...poolForm, pool_target_amount: e.target.value })}
                  placeholder="5000000"
                  required
                />
              </div>
              <div>
                <Label>Min. Investment (SAR)</Label>
                <Input
                  type="number"
                  value={poolForm.pool_minimum_investment}
                  onChange={(e) => setPoolForm({ ...poolForm, pool_minimum_investment: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Duration (months)</Label>
              <Input
                type="number"
                value={poolForm.pool_duration_months}
                onChange={(e) => setPoolForm({ ...poolForm, pool_duration_months: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowPoolDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">Create Pool</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Investor Dialog */}
      <Dialog open={showInvestorDialog} onOpenChange={setShowInvestorDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-green-600" />
              Add New Investor
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateInvestor} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Full Name *</Label>
                <Input
                  value={investorForm.name}
                  onChange={(e) => setInvestorForm({ ...investorForm, name: e.target.value })}
                  placeholder="Ahmed Al-Rashid"
                  required
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={investorForm.email}
                  onChange={(e) => setInvestorForm({ ...investorForm, email: e.target.value })}
                  placeholder="ahmed@example.com"
                  required
                />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input
                  value={investorForm.phone}
                  onChange={(e) => setInvestorForm({ ...investorForm, phone: e.target.value })}
                  placeholder="+966501234567"
                  required
                />
              </div>
              <div>
                <Label>Nationality</Label>
                <Input
                  value={investorForm.nationality}
                  onChange={(e) => setInvestorForm({ ...investorForm, nationality: e.target.value })}
                  placeholder="Saudi"
                />
              </div>
              <div>
                <Label>Username *</Label>
                <Input
                  value={investorForm.username}
                  onChange={(e) => setInvestorForm({ ...investorForm, username: e.target.value })}
                  placeholder="ahmed_investor"
                  required
                />
              </div>
              <div className="col-span-2">
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={investorForm.password}
                  onChange={(e) => setInvestorForm({ ...investorForm, password: e.target.value })}
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowInvestorDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">Add Investor</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Investment Dialog */}
      <Dialog open={showInvestmentDialog} onOpenChange={setShowInvestmentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              Add Investment to {selectedPool?.pool_name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddInvestment} className="space-y-4">
            <div>
              <Label>Investor *</Label>
              <Select
                value={investmentForm.investor_id}
                onValueChange={(v) => setInvestmentForm({ ...investmentForm, investor_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select investor" />
                </SelectTrigger>
                <SelectContent>
                  {investors.map(inv => (
                    <SelectItem key={inv.id} value={inv.id}>
                      {inv.name} ({inv.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Investment Amount (SAR) *</Label>
              <Input
                type="number"
                value={investmentForm.investment_amount}
                onChange={(e) => setInvestmentForm({ ...investmentForm, investment_amount: e.target.value })}
                placeholder="500000"
                required
              />
              {selectedPool && (
                <p className="text-xs text-muted-foreground mt-1">
                  Minimum: {selectedPool.pool_minimum_investment?.toLocaleString()} SAR
                </p>
              )}
            </div>
            <div>
              <Label>Investment Date *</Label>
              <Input
                type="date"
                value={investmentForm.investment_date}
                onChange={(e) => setInvestmentForm({ ...investmentForm, investment_date: e.target.value })}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowInvestmentDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">Add Investment</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Pool Details Dialog */}
      <Dialog open={showPoolDetailsDialog} onOpenChange={setShowPoolDetailsDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-purple-600" />
              {poolDetails?.pool_name}
            </DialogTitle>
          </DialogHeader>
          
          {poolDetails && (
            <div className="space-y-6">
              {/* Pool Info */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-purple-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-purple-600">
                    {poolDetails.pool_raised_amount?.toLocaleString()}
                  </p>
                  <p className="text-sm text-muted-foreground">Raised (SAR)</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">
                    {poolDetails.pool_target_amount?.toLocaleString()}
                  </p>
                  <p className="text-sm text-muted-foreground">Target (SAR)</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">
                    {poolDetails.investments?.length || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">Investors</p>
                </div>
              </div>

              {/* Investments */}
              <div>
                <h4 className="font-semibold mb-3">Investments</h4>
                {poolDetails.investments?.length > 0 ? (
                  <div className="space-y-2">
                    {poolDetails.investments.map((inv, idx) => {
                      const investor = poolDetails.investors?.find(i => i.id === inv.investor_id);
                      return (
                        <div key={idx} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-100 rounded-full">
                              <Users className="h-4 w-4 text-green-600" />
                            </div>
                            <div>
                              <p className="font-medium">{investor?.name || 'Unknown'}</p>
                              <p className="text-xs text-muted-foreground">{investor?.email}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold">{inv.investment_amount?.toLocaleString()} SAR</p>
                            <p className="text-xs text-muted-foreground">
                              {inv.ownership_percentage?.toFixed(2)}% ownership
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">No investments yet</p>
                )}
              </div>

              {/* Assets */}
              <div>
                <h4 className="font-semibold mb-3">Pool Assets</h4>
                {poolDetails.assets?.length > 0 ? (
                  <div className="space-y-2">
                    {poolDetails.assets.map((asset, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div>
                          <p className="font-medium">{asset.make} {asset.model}</p>
                          <p className="text-xs text-muted-foreground">
                            {asset.registration_number} • {asset.asset_type}
                          </p>
                        </div>
                        <Badge className={statusColors[asset.status] || 'bg-gray-100'}>
                          {asset.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">No assets linked yet</p>
                )}
              </div>

              {/* Rules Summary */}
              {poolDetails.rules && (
                <div>
                  <h4 className="font-semibold mb-3">Commercial Rules</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-muted-foreground">Profit Share (Actual)</p>
                      <p className="font-medium">
                        Company: {poolDetails.rules.profit_share_company_percent}% | 
                        Investor: {poolDetails.rules.profit_share_investor_percent}%
                      </p>
                    </div>
                    <div className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-muted-foreground">Document Share</p>
                      <p className="font-medium">
                        Company: {poolDetails.rules.document_share_company_percent}% | 
                        Investor: {poolDetails.rules.document_share_investor_percent}%
                      </p>
                    </div>
                    <div className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-muted-foreground">Payout Frequency</p>
                      <p className="font-medium capitalize">{poolDetails.rules.payout_frequency}</p>
                    </div>
                    <div className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-muted-foreground">ROI Priority</p>
                      <p className="font-medium">
                        {poolDetails.rules.roi_return_priority ? 'Yes - Investors First' : 'No'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPoolDetailsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pool Rules Dialog */}
      <Dialog open={showRulesDialog} onOpenChange={setShowRulesDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-blue-600" />
              Edit Pool Rules - {selectedPool?.pool_name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateRules} className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-3">Actual Profit Share</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Company %</Label>
                  <Input
                    type="number"
                    value={rulesForm.profit_share_company_percent}
                    onChange={(e) => setRulesForm({ 
                      ...rulesForm, 
                      profit_share_company_percent: parseFloat(e.target.value),
                      profit_share_investor_percent: 100 - parseFloat(e.target.value)
                    })}
                  />
                </div>
                <div>
                  <Label>Investor %</Label>
                  <Input
                    type="number"
                    value={rulesForm.profit_share_investor_percent}
                    onChange={(e) => setRulesForm({ 
                      ...rulesForm, 
                      profit_share_investor_percent: parseFloat(e.target.value),
                      profit_share_company_percent: 100 - parseFloat(e.target.value)
                    })}
                  />
                </div>
              </div>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold mb-3">Document Share (Official)</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Company %</Label>
                  <Input
                    type="number"
                    value={rulesForm.document_share_company_percent}
                    onChange={(e) => setRulesForm({ 
                      ...rulesForm, 
                      document_share_company_percent: parseFloat(e.target.value)
                    })}
                  />
                </div>
                <div>
                  <Label>Investor %</Label>
                  <Input
                    type="number"
                    value={rulesForm.document_share_investor_percent}
                    onChange={(e) => setRulesForm({ 
                      ...rulesForm, 
                      document_share_investor_percent: parseFloat(e.target.value)
                    })}
                  />
                </div>
              </div>
            </div>

            <div>
              <Label>Payout Frequency</Label>
              <Select
                value={rulesForm.payout_frequency}
                onValueChange={(v) => setRulesForm({ ...rulesForm, payout_frequency: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowRulesDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">Save Rules</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================
// FINANCE TAB COMPONENT
// ============================================

const FinanceTab = ({ pools, investors }) => {
  const [selectedPoolId, setSelectedPoolId] = useState('');
  const [financialSummary, setFinancialSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [showDistributionDialog, setShowDistributionDialog] = useState(false);
  const [distributionPreview, setDistributionPreview] = useState(null);
  
  const [expenseForm, setExpenseForm] = useState({
    expense_category: 'maintenance',
    expense_description: '',
    expense_amount: '',
    expense_date: new Date().toISOString().split('T')[0],
    vendor_name: ''
  });
  
  const [distributionAmount, setDistributionAmount] = useState('');

  const loadFinanceData = useCallback(async (poolId) => {
    if (!poolId) return;
    setLoading(true);
    try {
      const [summaryRes, expensesRes, revenueRes, payoutsRes] = await Promise.all([
        getPoolFinancialSummary(poolId),
        listPoolExpenses(poolId),
        listPoolRevenue(poolId),
        listPoolPayouts(poolId)
      ]);
      setFinancialSummary(summaryRes.data);
      setExpenses(expensesRes.data);
      setRevenue(revenueRes.data);
      setPayouts(payoutsRes.data);
    } catch (error) {
      console.error('Failed to load finance data:', error);
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedPoolId) {
      loadFinanceData(selectedPoolId);
    }
  }, [selectedPoolId, loadFinanceData]);

  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      await addPoolExpense(selectedPoolId, {
        ...expenseForm,
        expense_amount: parseFloat(expenseForm.expense_amount)
      });
      toast.success('Expense added');
      setShowExpenseDialog(false);
      setExpenseForm({
        expense_category: 'maintenance',
        expense_description: '',
        expense_amount: '',
        expense_date: new Date().toISOString().split('T')[0],
        vendor_name: ''
      });
      loadFinanceData(selectedPoolId);
    } catch (error) {
      toast.error('Failed to add expense');
    }
  };

  const handleCalculateDistribution = async () => {
    if (!distributionAmount || parseFloat(distributionAmount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    try {
      const res = await calculateDistribution(selectedPoolId, parseFloat(distributionAmount));
      setDistributionPreview(res.data);
    } catch (error) {
      toast.error('Failed to calculate distribution');
    }
  };

  const handleGeneratePayouts = async () => {
    try {
      const period = new Date().toISOString().slice(0, 7);
      await generatePayouts(selectedPoolId, parseFloat(distributionAmount), period);
      toast.success('Payout entries created');
      setShowDistributionDialog(false);
      setDistributionPreview(null);
      setDistributionAmount('');
      loadFinanceData(selectedPoolId);
    } catch (error) {
      toast.error('Failed to generate payouts');
    }
  };

  const handleApprovePayout = async (payoutId) => {
    try {
      await approvePoolPayout(selectedPoolId, payoutId);
      toast.success('Payout approved');
      loadFinanceData(selectedPoolId);
    } catch (error) {
      toast.error('Failed to approve payout');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      await deletePoolExpense(selectedPoolId, expenseId);
      toast.success('Expense deleted');
      loadFinanceData(selectedPoolId);
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const expenseCategoryLabels = {
    maintenance: 'Maintenance',
    operator_salary: 'Operator Salary',
    fuel: 'Fuel',
    insurance: 'Insurance',
    operational: 'Operational',
    other: 'Other'
  };

  return (
    <div className="space-y-6">
      {/* Pool Selector */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="flex-1 max-w-sm">
              <Label>Select Pool</Label>
              <Select value={selectedPoolId} onValueChange={setSelectedPoolId}>
                <SelectTrigger data-testid="finance-pool-select">
                  <SelectValue placeholder="Choose a pool to view financials" />
                </SelectTrigger>
                <SelectContent>
                  {pools.map(pool => (
                    <SelectItem key={pool.id} value={pool.id}>
                      {pool.pool_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedPoolId && (
              <div className="flex gap-2 mt-6">
                <Button variant="outline" onClick={() => setShowExpenseDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Expense
                </Button>
                <Button onClick={() => setShowDistributionDialog(true)}>
                  <Calculator className="h-4 w-4 mr-2" />
                  Calculate Distribution
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {!selectedPoolId ? (
        <Card className="p-12 text-center">
          <Calculator className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold">Select a Pool</h3>
          <p className="text-muted-foreground">Choose an investment pool to view financial details</p>
        </Card>
      ) : loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading financial data...</div>
      ) : financialSummary ? (
        <>
          {/* Financial Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <ArrowUpCircle className="h-8 w-8 text-green-500" />
                  <div>
                    <p className="text-2xl font-bold">{financialSummary.revenue_collected?.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">Revenue Collected (SAR)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-red-500">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <ArrowDownCircle className="h-8 w-8 text-red-500" />
                  <div>
                    <p className="text-2xl font-bold">{financialSummary.total_expenses?.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">Total Expenses (SAR)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Wallet className="h-8 w-8 text-blue-500" />
                  <div>
                    <p className="text-2xl font-bold">{financialSummary.net_distributable?.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">Net Distributable (SAR)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <TrendingUp className="h-8 w-8 text-purple-500" />
                  <div>
                    <p className="text-2xl font-bold">{financialSummary.roi_progress_percent?.toFixed(1)}%</p>
                    <p className="text-sm text-muted-foreground">ROI Progress</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ROI Progress */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>ROI Recovery Progress</span>
                <Badge className={financialSummary.is_profit_stage ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}>
                  {financialSummary.stage_label}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all"
                    style={{ width: `${Math.min(financialSummary.roi_progress_percent || 0, 100)}%` }}
                  />
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="text-center p-3 bg-muted/30 rounded-lg">
                    <p className="text-muted-foreground">Target</p>
                    <p className="font-bold">{financialSummary.roi_target?.toLocaleString()} SAR</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-muted-foreground">Recovered</p>
                    <p className="font-bold text-green-600">{financialSummary.roi_recovered?.toLocaleString()} SAR</p>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <p className="text-muted-foreground">Remaining</p>
                    <p className="font-bold text-orange-600">{financialSummary.roi_remaining?.toLocaleString()} SAR</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Expenses and Payouts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Expenses */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowDownCircle className="h-5 w-5 text-red-500" />
                  Expenses ({expenses.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {expenses.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No expenses recorded</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {expenses.map(exp => (
                      <div key={exp.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div>
                          <p className="font-medium">{exp.expense_description}</p>
                          <p className="text-xs text-muted-foreground">
                            {expenseCategoryLabels[exp.expense_category] || exp.expense_category} • {exp.expense_date}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-red-600">-{exp.expense_amount?.toLocaleString()}</span>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-7 w-7 p-0"
                            onClick={() => handleDeleteExpense(exp.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Payouts */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wallet className="h-5 w-5 text-green-500" />
                  Payouts ({payouts.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {payouts.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No payouts yet</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {payouts.map(pay => (
                      <div key={pay.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div>
                          <p className="font-medium">{pay.investor_name || 'Investor'}</p>
                          <p className="text-xs text-muted-foreground">
                            {pay.payout_type === 'roi_repayment' ? 'ROI Repayment' : 'Profit Share'} • {pay.period}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-green-600">+{pay.payout_amount?.toLocaleString()}</span>
                          {pay.status === 'pending' ? (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleApprovePayout(pay.id)}
                            >
                              <FileCheck className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                          ) : (
                            <Badge className="bg-green-100 text-green-800">Paid</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Investor Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Investor Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {financialSummary.investments?.map((inv, idx) => {
                  const investor = investors.find(i => i.id === inv.investor_id);
                  return (
                    <div key={idx} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-full">
                          <Users className="h-4 w-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium">{investor?.name || 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground">
                            {inv.ownership_percent?.toFixed(2)}% ownership • Invested: {inv.investment_amount?.toLocaleString()} SAR
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">+{inv.roi_recovered?.toLocaleString()} SAR</p>
                        <p className="text-xs text-muted-foreground">ROI Received</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </>
      ) : null}

      {/* Add Expense Dialog */}
      <Dialog open={showExpenseDialog} onOpenChange={setShowExpenseDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowDownCircle className="h-5 w-5 text-red-500" />
              Add Expense
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddExpense} className="space-y-4">
            <div>
              <Label>Category</Label>
              <Select
                value={expenseForm.expense_category}
                onValueChange={(v) => setExpenseForm({ ...expenseForm, expense_category: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="operator_salary">Operator Salary</SelectItem>
                  <SelectItem value="fuel">Fuel</SelectItem>
                  <SelectItem value="insurance">Insurance</SelectItem>
                  <SelectItem value="operational">Operational</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description *</Label>
              <Input
                value={expenseForm.expense_description}
                onChange={(e) => setExpenseForm({ ...expenseForm, expense_description: e.target.value })}
                placeholder="e.g., Monthly driver salary"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (SAR) *</Label>
                <Input
                  type="number"
                  value={expenseForm.expense_amount}
                  onChange={(e) => setExpenseForm({ ...expenseForm, expense_amount: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={expenseForm.expense_date}
                  onChange={(e) => setExpenseForm({ ...expenseForm, expense_date: e.target.value })}
                  required
                />
              </div>
            </div>
            <div>
              <Label>Vendor Name</Label>
              <Input
                value={expenseForm.vendor_name}
                onChange={(e) => setExpenseForm({ ...expenseForm, vendor_name: e.target.value })}
                placeholder="Optional"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowExpenseDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">Add Expense</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Distribution Calculator Dialog */}
      <Dialog open={showDistributionDialog} onOpenChange={setShowDistributionDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-500" />
              Calculate Distribution
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Amount to Distribute (SAR)</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  value={distributionAmount}
                  onChange={(e) => setDistributionAmount(e.target.value)}
                  placeholder="Enter amount"
                />
                <Button onClick={handleCalculateDistribution}>
                  Calculate
                </Button>
              </div>
            </div>

            {distributionPreview && (
              <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Stage:</span>
                  <Badge className={distributionPreview.stage === 'profit_sharing' ? 'bg-green-100' : 'bg-blue-100'}>
                    {distributionPreview.stage === 'profit_sharing' ? 'Profit Sharing' : 'ROI Recovery'}
                  </Badge>
                </div>
                
                <div className="space-y-2">
                  <p className="font-semibold">Distribution Preview:</p>
                  {distributionPreview.distribution?.map((item, idx) => (
                    <div key={idx} className="flex justify-between p-2 bg-white rounded">
                      <span>{item.investor_name || item.entity || 'Unknown'}</span>
                      <span className="font-bold">{item.calculated_amount?.toLocaleString()} SAR</span>
                    </div>
                  ))}
                </div>

                <Button className="w-full" onClick={handleGeneratePayouts}>
                  <FileCheck className="h-4 w-4 mr-2" />
                  Generate Payout Entries
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDistributionDialog(false);
              setDistributionPreview(null);
              setDistributionAmount('');
            }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvestorPoolsPage;
